import { Link } from 'react-router-dom';
import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';
import { delta, formatShare } from '../../utils/period';

interface StatTileProps {
  label: string;
  current: number | null;
  previous?: number | null;
  /** Formats current and previous values for display. */
  format: (value: number | null) => string;
  /** Whether a rise is good news, bad news, or neither. */
  good?: 'up' | 'down' | 'neutral';
  /** A second line under the value, e.g. "40% verified". */
  detail?: string;
  /** Drill-down link to the rows behind the figure. */
  to?: string;
  /** Short note on what the figure does and doesn't cover. */
  note?: string;
}

/**
 * One headline figure with its movement against the previous period. The
 * arrow and words carry the direction; color only reinforces it.
 */
const StatTile = ({ label, current, previous, format, good = 'up', detail, to, note }: StatTileProps) => {
  const d = previous === undefined ? null : delta(current, previous);
  const tone =
    !d || d.direction === 'none' || d.direction === 'flat' || good === 'neutral'
      ? 'neutral'
      : (d.direction === 'up') === (good === 'up')
        ? 'good'
        : 'bad';
  const Icon = d?.direction === 'up' ? ArrowUpRight : d?.direction === 'down' ? ArrowDownRight : Minus;

  const body = (
    <>
      <div className="stat-tile-label">{label}</div>
      <div className="stat-tile-value">{format(current)}</div>
      {detail && <div className="stat-tile-detail">{detail}</div>}
      {d && (
        <div className={`stat-tile-delta stat-tile-delta-${tone}`}>
          <Icon size={14} aria-hidden="true" />
          <span>
            {d.direction === 'none'
              ? 'No comparison'
              : d.direction === 'flat'
                ? 'No change'
                : d.change === null
                  ? `${d.direction === 'up' ? 'Up' : 'Down'} from ${format(previous ?? null)}`
                  : `${d.change > 0 ? '+' : ''}${formatShare(d.change)} vs ${format(previous ?? null)}`}
          </span>
        </div>
      )}
      {note && <div className="stat-tile-note">{note}</div>}
    </>
  );

  return to ? (
    <Link to={to} className="stat-tile stat-tile-link" title="See the rows behind this figure">
      {body}
    </Link>
  ) : (
    <div className="stat-tile">{body}</div>
  );
};

export default StatTile;
